import { useCallback, useState } from 'react'
import { Abi, parseEventLogs } from 'viem'
import { useAccount } from 'wagmi'

import abiRarityRewardFactory from 'abi/abi-reward-factory.json'
import { SettingEnum } from 'constants/setting'
import { publicWallet } from 'libs/viem/config'
import { CreateRewardForm } from 'modules/pto-rewards/form-create'
import { logError, logInfo } from 'utils/logger'

import { customErrorContract, useContractViem } from './contract/use-contract-viem'
import { useAppSetting } from './setting/use-app-setting'

export interface RewardPoolArguments {
  poolId?: string
  PTOAddress: string
  USDCAddress: string
  amounts: Array<bigint | number>
  claimStartTimes: string[]
  ownerAddress: string
  rarityIds: number[]
}

export function useCreateReward() {
  const { address } = useAccount()

  const [isLoading, setIsLoading] = useState(false)

  const { getSetting } = useAppSetting()
  const rarityRewardFactoryAddress = getSetting(SettingEnum.RARITY_REWARD_FACTORY_ADDRESS) as
    | `0x${string}`
    | null

  const rarityRewardContract = useContractViem(
    rarityRewardFactoryAddress,
    abiRarityRewardFactory as Abi
  )

  const createRewardPoolOnChain = useCallback(
    async (args: RewardPoolArguments[keyof RewardPoolArguments][]) => {
      try {
        setIsLoading(true)

        if (!address || !rarityRewardContract) {
          logError('createRewardPoolOnChain: Missing required data', {
            hasAddress: !!address,
            hasContract: !!rarityRewardContract,
            contractAddress: rarityRewardFactoryAddress,
          })
          return
        }

        const nonce = BigInt(
          await publicWallet.getTransactionCount({
            address,
            blockTag: 'pending',
          })
        )

        logInfo('[createRewardPoolOnChain] Contract call details:', {
          contractAddress: rarityRewardFactoryAddress,
          callerAddress: address,
          nonce: nonce.toString(),
          args: JSON.stringify(args, (_, v) => (typeof v === 'bigint' ? v.toString() : v)),
        })

        const txHash = await rarityRewardContract.write.createRewardPool(args, {
          account: address,
          nonce,
        })

        logInfo('[createRewardPoolOnChain] Transaction submitted:', txHash)

        const receipt = await publicWallet.waitForTransactionReceipt({
          hash: txHash,
          confirmations: 1,
        })

        logInfo('[createRewardPoolOnChain] Transaction receipt:', {
          status: receipt.status,
          blockNumber: receipt.blockNumber?.toString(),
          gasUsed: receipt.gasUsed?.toString(),
        })

        const logs: any = parseEventLogs({
          abi: abiRarityRewardFactory,
          logs: receipt.logs,
          eventName: 'RewardPoolCreated',
        })

        if (logs.length > 0) {
          const poolAddress = logs[0].args.rewardPool

          return poolAddress
        }
      } catch (error: any) {
        const errorDetails = {
          message: error?.message,
          cause: error?.cause,
          shortMessage: error?.shortMessage,
          metaMessages: error?.metaMessages,
          details: error?.details,
          code: error?.code,
          name: error?.name,
          contractAddress: rarityRewardFactoryAddress,
          callerAddress: address,
        }
        logError('[createRewardPoolOnChain] Contract call failed:', errorDetails)
        logError('createRewardPoolOnChain failed', errorDetails)

        const errorMessage = customErrorContract(error)
        logError('Error while create rewards', errorMessage)
      } finally {
        setIsLoading(false)
      }
    },
    [address, rarityRewardContract, rarityRewardFactoryAddress]
  )

  const preparePayload = (data: CreateRewardForm) => {
    const validRarities = data.rarities.filter((rarity) => rarity.amount)
    logInfo('data', data)
    logInfo('validRarities', validRarities)

    return {
      tokenizeId: data.pto,
      ownerAddress: data.ownerAddress,
      asset: data.asset,
      rewards: validRarities.map((rarity) => ({
        rarity: rarity.rarityId,
        rewardAmount: rarity.amount,
        activationDate: rarity.activationDate
          ? new Date(rarity.activationDate).toISOString().replace('T', ' ').substring(0, 19)
          : undefined,
      })),
    }
  }

  return {
    isLoading,
    preparePayload,
    createRewardPoolOnChain,
  }
}
